"use client";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar, CalendarProps } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatDateForPicker } from "@/utils/date.utils";

interface DatePickerProps {
  value?: Date;
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  shouldDisableDate?: (date: Date) => boolean;
  calendarProps?: Omit<CalendarProps, 'mode' | 'selected' | 'onSelect' | 'disabled' | 'initialFocus'>;
  className?: string;
  dateFormat?: string;
  popoverSide?: "top" | "bottom" | "left" | "right";
}

const DatePicker = React.forwardRef<
  HTMLButtonElement,
  DatePickerProps
>(({
  value,
  onSelect,
  placeholder: placeholderKey = "datePicker.pickDatePlaceholder",
  disabled = false,
  shouldDisableDate,
  calendarProps,
  className,
  dateFormat,
  popoverSide = "top",
}, ref) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const placeholder = t(placeholderKey);

  // Create a simple check that combines shouldDisableDate with the disabled prop
  const isDateDisabled = shouldDisableDate 
    ? (date: Date) => disabled || shouldDisableDate(date)
    : disabled;

  const handleSelect = (date: Date | undefined) => {
    onSelect?.(date);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal border border-input bg-background shadow-none",
            "hover:!bg-muted hover:!text-foreground",
            "data-[state=open]:!bg-muted data-[state=open]:!text-foreground",
            "focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-0",
            !value &&
              "text-muted-foreground hover:!text-muted-foreground data-[state=open]:!text-muted-foreground",
            value && "text-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-5 w-5 text-primary" />
          {value ? (
            <span className="truncate font-medium">{formatDateForPicker(value, dateFormat)}</span>
          ) : (
            <span className="truncate">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent
        className="w-auto p-0 border border-border shadow-lg rounded-lg"
        align="start"
        side={popoverSide}
        onPointerDownOutside={(event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.closest("[data-radix-select-content], [role='listbox']")) {
            event.preventDefault();
          }
        }}
      >
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          disabled={isDateDisabled}
          initialFocus
          showOutsideDays={false}      
          {...calendarProps}
        />
      </PopoverContent>
    </Popover>
  );
});
DatePicker.displayName = "DatePicker";

export { DatePicker }; 